import React, {useState} from 'react';
import {Divider, IconButton, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ManageSuppliersService} from '@store/suppliers/services';

import {AddWhite, Delete} from '@icons/index';
import {Sizes} from '@constants/sizes.constants';

import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import ManagePriceModal from '@screens/admin/suppliers/details/catalog/manageService/components/ManagePrice.modal';
import {Edit} from '@mui/icons-material';
import SelectComponent from '@components/inputs/select/Select.component';
import {UnitOfMeasure} from '@store/settings/serviceKinds';

interface PricesFormProps {
  isCreation?: boolean;
  service: ManageSuppliersService;
  updateService: (data: ManageSuppliersService) => void;
}

const PricesForm = (props: PricesFormProps) => {
  const {isCreation, service, updateService} = props;

  const {t} = useTranslation();

  const [showPrice, setShowPrice] = useState<number | boolean>(false);

  return (
    <>
      <Stack py="20px" spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize={16} fontWeight="700">
            {t('suppliers.details.catalog.manage.price')}
          </Typography>
          <IconButton aria-label="small-square-text" centerRipple={false} onClick={() => setShowPrice(true)}>
            <AddWhite/>
          </IconButton>
        </Stack>
        <Stack spacing={4}>
          {service.prices?.sort((p1, p2) => (p1.unitCostInCents || 0) - (p2.unitCostInCents || 0)).map((price, index) => (
            <Stack key={price.id || `price ${index}`} spacing={1}>
              <Stack spacing={2}>
                <NumberTextFieldComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('suppliers.details.catalog.manage.puBuy')}
                  value={price.unitCost ?? undefined}
                  endAdornment={(
                    <Typography fontSize={14} fontWeight="400">
                      {price.currency}
                    </Typography>
                  )}
                  readOnly
                  required/>
                <SelectComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('suppliers.details.catalog.manage.unit')}
                  placeholder="-"
                  value={service.serviceKind?.execUnitName1 || ''}
                  items={UnitOfMeasure.selectItems()}
                  readOnly/>
                {(price.minQuantity != null || price.maxQuantity != null) && (
                  <NumberTextFieldComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('suppliers.details.catalog.manage.minQuantity')}
                    value={price.minQuantity ?? undefined}
                    readOnly/>
                )}
                {(price.minQuantity != null || price.maxQuantity != null) && (
                  <NumberTextFieldComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('suppliers.details.catalog.manage.maxQuantity')}
                    value={price.maxQuantity ?? undefined}
                    readOnly/>
                )}
              </Stack>
              <Stack direction="row" alignSelf="flex-end">
                <IconButton onClick={() => setShowPrice(index)}>
                  <Edit color="primary"/>
                </IconButton>
                {isCreation && (
                  <IconButton
                    onClick={() => updateService({prices: (service.prices || []).filter((p, pIndex) => pIndex !== index)})}>
                    <Delete/>
                  </IconButton>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
      {showPrice !== false && (
        <ManagePriceModal
          handleClose={() => setShowPrice(false)}
          price={typeof showPrice === 'boolean' ? undefined : service.prices?.[showPrice]}
          unit={service.serviceKind?.execUnitName1}
          handleSaved={(price) => {
            if (typeof showPrice === 'boolean') {
              updateService({prices: (service.prices || []).concat(price)});
            } else {
              updateService({
                prices: (service.prices || []).map((p, pIndex) =>
                  pIndex === showPrice ? price : p
                )
              });
            }
          }}/>
      )}
    </>
  );
};

export default PricesForm;
